import $ from 'jquery';

import { sprintf } from '@wordpress/i18n';

import {
	getItemsByIntegration,
	getDOMElements,
	restCall,
	replaceCurlyBraces,
	openInNewTab,
	recipeSetRequiresUser
} from '../common/utilities.js';

import { getActionsOrder } from '@builder-legacy/item/sortability/utilities';

import { getConditionsGroupById, updateRemoteConditionsMeta } from '@builder-legacy/item/conditions/utilities';

// Import utilities
const {
	isDefined,
	isEmpty,
	addParametersUTM,
	addParametersToURL
} = _uo.utility;

import Modal          from '../common/modal.js';

import ItemCreatorIntegrations from './integrations-grid.js';
import ItemHeaderMenu          from './action-menu.js';
import ItemHeaderIcon          from './integration-icon.js';
import ItemHeaderTag           from './integration-name.js';
import Triggers 	           from './triggers.js';
import Actions 		           from './actions.js';

class ItemCreatorTaskSelector {
	constructor( itemType, integrationID, $currentItem, attributes = {} ){
		this.itemType 		= itemType;
		this.integrationID 	= integrationID;
		this.$currentItem 	= $currentItem;
		this.attributes     = attributes;

		// By this step, remove the URL parameters added
		// while connecting a premium integration
		this.removeConnectingIntegrationsParameters();

		// Render DOM elements
		this.render();
	}

	render(){
		// Get content
		const content = {
			// The section title. Either "Select a trigger" or "Select an action"
			title: this.itemType == 'trigger' ? UncannyAutomator._core.i18n.trigger.select : UncannyAutomator._core.i18n.action.select,

			// Icon. The integration icon
			$icon: new ItemHeaderIcon( this.integrationID ),

			// Integration name tag
			$integrationNameTag: new ItemHeaderTag( {
				integrationId: this.integrationID,
			} ),

			// The action menu. The three dots on the upper right corner.
			$actionMenu: new ItemHeaderMenu( this.itemType )
		}

		// Create container
		const $container = document.createElement( 'div' );
		$container.classList.add( 'uap-items', 'uap-items--create-new' );

		// Create item
		const $item = document.createElement( 'div' );
		$item.classList.add( 'uap-item', 'uap-item--create-new' );
		$container.appendChild( $item );

		// Add data useful for the JS
		$item.setAttribute( 'data-integration', this.integrationID );

		// Save it as a property
		this.$item = $item;

		// Add content
		$item.insertAdjacentHTML( 'beforeEnd', `
			<div class="uap-item-inner">
				<div class="uap-item-sidebar"></div>
				<div class="uap-item-top-header">
					<div class="uap-item-top-header-left"></div>
					<div class="uap-item-top-header-right"></div>
				</div>
				<div class="uap-item-inner-content"></div>
			</div>
		` );

		// Icon. The integration icon
		$item.querySelector( '.uap-item-sidebar' )
			.appendChild( content.$icon );

		// Add integration name tag
		$item.querySelector( '.uap-item-top-header-left' )
			.appendChild( content.$integrationNameTag );

		// Add action menu
		$item.querySelector( '.uap-item-top-header-right' )
			.appendChild( content.$actionMenu.get(0) );

		// Add inner content
		$item.querySelector( '.uap-item-inner-content' )
			.appendChild( this.createItemsList().get(0) );

		// Replace the current item with the new content
		this.$currentItem
			.replaceWith( $container );
	}

	createItemsList(){
		let items, searchPlaceholder, unlockItems;

		// Get data depending on the item type
		if ( this.itemType == 'trigger' ){
			items 	           = getItemsByIntegration( this.integrationID, 'triggers' );
			searchPlaceholder  = UncannyAutomator._core.i18n.trigger.search;
			unlockItems        = UncannyAutomator._core.i18n.proOnly.unlockTriggers;
		}
		else if ( this.itemType == 'action' ){
			items              = getItemsByIntegration( this.integrationID, 'actions' );
			searchPlaceholder  = UncannyAutomator._core.i18n.action.search;
			unlockItems        = UncannyAutomator._core.i18n.proOnly.unlockActions;
		}

		// Create the main container
		const $container   = $( `
			<div class="item-options item-options--nopadding item-options--noborder">
				<div class="form-element">
					<div class="item__task-selector">
						<div class="item-task-selector__search">
							<input
								type="text"
								class="item-task-selector__search-input"
								placeholder="${ searchPlaceholder }"
							>
						</div>
						<div class="item-task-selector__options">
							<div class="item-task-selector-options__group item-task-selector-options__group--available"></div>
							<div class="item-task-selector-options__group item-task-selector-options__group--unavailable">
								<div class="item-task-selector-options__group-items"></div>
							</div>
							<div class="item-task-selector__option item-task-selector__option--no-results item-task-selector__option--hide">
								${ UncannyAutomator._core.i18n.noResults }
							</div>
						</div>
					</div>
				</div>
			</div>
		` );

		// Get important elements
		const $taskSelector     = $container.find( '.item__task-selector' ),
			  $searchField      = $container.find( '.item-task-selector__search-input' ),
			  $itemsList        = {
				container: $container.find( '.item-task-selector__options' ),
				available: $container.find( '.item-task-selector-options__group--available' ),
				unavailable: {
					container: $container.find( '.item-task-selector-options__group--unavailable' ),
					list:      $container.find( '.item-task-selector-options__group-items' )
				},
				noResults: $container.find( '.item-task-selector__option--no-results' )
			  };

		// Check if it has available items
		if ( items.hasAvailableItems ){
			// Sort available items
			// Move the ones that require user data to the bottom if the user has Automator free
			// and this is an anonymous recipe
			if (
				// Check if WP is ready
				( ! UncannyAutomator._site.automator.has_pro )
				// Check if the recipe is anonymous
				&& UncannyAutomator._recipe.recipe_type == 'anonymous'
			){
				const itemsThatRequireUserData     = [],
					  itemsThatDontRequireUserData = [];

				items.available.forEach(( item ) => {
					if ( item.requires_user ){
						itemsThatRequireUserData.push( item );
					}
					else {
						itemsThatDontRequireUserData.push( item );
					}
				});

				items.available = [ ...itemsThatDontRequireUserData, ...itemsThatRequireUserData ];
			}

			// Check if it's inside a user loop
			const itemIsInsideUserLoop = UncannyAutomator._core.recipe.item.loop.isItemInsideUserLoop( this.attributes.parentID || 0 );

			// Append available items to the list container
			$itemsList.available.append( items.available.map(( item ) => {
				// Check if it's a Pro item
				const isPro = item.is_pro;

				// Check if it's an Elite item?
				const isElite = item.is_elite;

				// Check if the user can't add this action to this recipe
				// This happens if the action requires user data, but the user has Automator free
				const cannotAddRequiresUserData = (
					/**
					 * Check if this is not the first level
					 * @todo Once the new object is used, check if it's actually a loop and not a filter. Also check it's not a "posts" loop. Only "users" loops won't require user data in everyone recipes
					 */
					! itemIsInsideUserLoop && 

					// Check it has Pro
					( ! UncannyAutomator._site.automator.has_pro )
					// Check if the recipe is anonymous
					&& UncannyAutomator._recipe.recipe_type == 'anonymous'
					// Check if the action requires user data
					&& item.requires_user
				);

				// Check if we should add the "Requires" user data tag
				let shouldAddRequiresUserDataTag = 
					/**
					 * Check if this is not the first level
					 * @todo Once the new object is used, check if it's actually a loop and not a filter. Also check it's not a "posts" loop. Only "users" loops won't require user data in everyone recipes
					 */
					( ! itemIsInsideUserLoop ) && 

					UncannyAutomator._recipe.recipe_type == 'anonymous' && 
					! UncannyAutomator.recipe.requiresUserData && 
					item.requires_user;

				// Create container
				const $item = $( `
					<div class="item-task-selector__option ${ isPro ? 'item-task-selector__option--pro' : '' } ${ item.requires_user ? 'item-task-selector__option--requires-user' : '' } ${ cannotAddRequiresUserData ? 'item-task-selector__option-cannot-add' : '' }"
						data-search="${ item.select_option_name.toLowerCase().replace( /\s+|{|}/g, '' ) }"
						data-integration-id="${ this.integrationID }"
						data-item-id="${ item.code }">
						<div class="item-task-selector__option-sentence" dir="auto">
							${ cannotAddRequiresUserData ? '<uo-icon id="lock"></uo-icon>' : '' }
							${ replaceCurlyBraces( item.select_option_name, '<span class="item-task-selector__option-variable">', '</span>' ) } ${ isPro ? `<span class="item-task-selector-option__badge">${ UncannyAutomator._core.i18n.proOnly.pro }</span>` : '' } ${ isElite ? `<span class="item-task-selector-option__badge">${ UncannyAutomator._core.i18n.eliteOnly.elite }</span>` : '' }
						</div>
						<div class="item-task-selector__option-properties">
							${ 
								shouldAddRequiresUserDataTag ? 
								`<span class="item-task-selector-option__requires-user">${ UncannyAutomator._core.i18n.needsUserData }</span>` : 
								'' 
							}
						</div>
					</div>
				` );

				// Listen clicks on the container
				$item.on( 'click', () => {
					// Add loading class to the parent container
					$taskSelector.addClass( 'item__task-selector--loading' );

					// Add loading class to the container
					$item.addClass( 'item-task-selector__option--loading' );

					// Get the correct request code
					const restRequestCode = [ 'trigger', 'action' ].includes( this.itemType ) ? `add-new-${ this.itemType }` : '';
					
					// Save the value of the fields with default values
					// in the item meta
					let defaultMeta = {}
					$.each( item.options, ( index, option ) => {
						if ( isDefined( option.default_value ) ){
							defaultMeta[ option.option_code ] = option.default_value;
						}
					});

					// Check if the trigger is an anonymous trigger
					if ( item.type == 'anonymous' ){
						// Save the "can_log_in_new_user" value
						defaultMeta.can_log_in_new_user = isDefined( item.can_log_in_new_user ) ? item.can_log_in_new_user : true;
					}

					// Do REST call to add the item
					const addItemREST = ( onSuccess, onFail ) => {
						UncannyAutomator._core.recipe.item.createItem( 
							this.itemType, 
							item.code,
							{
								parentID: isDefined( this.attributes.parentID ) ? this.attributes.parentID : UncannyAutomator._recipe.recipe_id,
								actionsOrder: getActionsOrder( true ),
								defaultMeta: Object.assign( {}, defaultMeta, {
									integration_name: UncannyAutomator.integrations[ this.integrationID ].name,
									sentence: 		  item.sentence
								} )
							}
						).then(( response ) => {
							// Clear the tokens cache
							UncannyAutomator._cache.tokens = [];

							// Check if this is an action that we should add inside a conditions block
							if ( this.itemType === 'action' && ! isEmpty( this.attributes.conditionsGroupID ) ) {
								// Get the conditions block
								const conditionsBlock = getConditionsGroupById( { conditionsGroupId: this.attributes.conditionsGroupID } );

								// Add action
								conditionsBlock.addAction( response.post_ID );

								// Render the UI again right away after adding the action to the conditions block
								new Actions();

								// Update data in the database
								updateRemoteConditionsMeta( {
									onSuccess: () => {
										new Actions();

										// Check if the ID of the new item is defined
										this.openFirstGroupsOfFields( response.post_ID );

										// Check if the user defined a custom callback
										if ( isDefined( onSuccess ) ){
											onSuccess( response );
										}
									}
								} );

								// Stop here
								return;
							}

							// Render both metaboxes again
							// This will close the options panel if it's opened
							new Triggers();
							new Actions();

							// Check if the ID of the new item is defined
							this.openFirstGroupsOfFields( response.post_ID );

							// Send custom event to the document
							document.dispatchEvent( new CustomEvent( 'uap/builder/legacy/item-created', {
								detail: {
									itemID: response.post_ID,
									itemType: this.itemType,
									integrationCode: this.integrationID,
									itemCode: item.code
								}
							} ) );

							// Check if the user defined a custom callback
							if ( isDefined( onSuccess ) ){
								onSuccess( response );
							}
						}).catch( e => {
							// Remove loading class to the parent container
							$taskSelector.removeClass( 'item__task-selector--loading' );

							// Remove loading class to the container
							$item.removeClass( 'item-task-selector__option--loading' );

							new Triggers();

							// Check if the user defined a custom callback
							if ( isDefined( onFail ) ){
								onFail( response );
							}
						} );
					}
						
					// Check if we have to show a modal to set a user before adding the item
					if ( 
						/**
						 * Check if this is not the first level
						 * @todo Once the new object is used, check if it's actually a loop and not a filter. Also check it's not a "posts" loop. Only "users" loops won't require user data in everyone recipes
						 */
						( ! itemIsInsideUserLoop ) && 

						// Check if the item that the user wants to add is an action
						this.itemType == 'action'
						// Check if the recipe is anonymous
						&& UncannyAutomator._recipe.recipe_type == 'anonymous' 
						// Check if the recipe currently DOES NOT need user data
						// Otherwise, don't show the modal as the user already knows
						// the recipe needs user data
						&& ! UncannyAutomator.recipe.requiresUserData
						// Check if the action the user is trying to add requires user data
						&& item.requires_user
					){
						// Create modal
						let modal;

						// Remove loading class to the parent container
						$taskSelector.removeClass( 'item__task-selector--loading' );

						// Remove loading class to the container
						$item.removeClass( 'item-task-selector__option--loading' );

						// Check if WP is ready
						if ( ! UncannyAutomator._site.automator.has_pro ){
							modal = new Modal({
								title:   `${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.forbidden.title } <span class="needs-user-data-modal-pro-badge">${ UncannyAutomator._core.i18n.proOnly.pro }</span>`,
								content: `
									<div class="needs-user-data-modal">
										<div class="needs-user-data-modal__title">${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.forbidden.content.mustRunOnUser }</div>
										<div class="needs-user-data-modal__sentence">
											${ replaceCurlyBraces( item.select_option_name, '<span>', '</span>' ) }
										</div>
										<div class="needs-user-data-modal__disclaimer">
											<p>
												<div class="description">${ sprintf( UncannyAutomator._core.i18n.userSelector.userDataModal.action.forbidden.content.description, UncannyAutomator._core.i18n.uncannyAutomatorPro ) } <a href="${ addParametersUTM( 'https://automatorplugin.com/pricing/', { 'medium': 'select_an_item', 'campaign': 'update_to_pro', 'content': 'requires_user_data',
											}) }" target="_blank">${ UncannyAutomator._core.i18n.upgradeNow } <uo-icon id="external-link"></uo-icon></a></div>
											</p>
											<p>
												<div class="description">${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.forbidden.content.upgradeLater } <a href="${ addParametersUTM( 'https://automatorplugin.com/knowledge-base/creating-a-recipe/#Recipe_types', { 'medium': 'select_an_item', 'content': 'requires_user_data-learn-more',
											}) }" target="_blank">${ UncannyAutomator._core.i18n.learnMore } <uo-icon id="external-link"></uo-icon></a></div>
											</p>
										</div>
									</div>
								`,
								buttons: {
									cancel:  UncannyAutomator._core.i18n.cancel,
									confirm: UncannyAutomator._core.i18n.userSelector.userDataModal.action.forbidden.confirmButton,
								},
							}, {
								size: 'large'
							});

							modal.setEvents({
								onConfirm: () => {
									// Close and destroy modal
									modal.destroy();

									// Open link in new tab
									openInNewTab( addParametersUTM( 'https://automatorplugin.com/pricing/', { 'medium': 'select_an_item', 'campaign': 'update_to_pro', 'content': 'requires_user_data',
											}) );
								},
								onCancel: () => {
									// Close and destroy modal
									modal.destroy();
								},
							});
						}
						else {
							// Check if we should set the recipe to draft when the user adds the action
							// We're doing this because the user will need to fill the User Selector
							const actionWillSetRecipeToDraft = UncannyAutomator._recipe.is_recipe_on;

							modal = new Modal({
								title:   `${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.allowed.title } <span class="needs-user-data-modal-pro-badge needs-user-data-modal-pro-badge--gray">${ UncannyAutomator._core.i18n.proOnly.pro }</span>`,
								content: `
									<div class="needs-user-data-modal">
										<div class="needs-user-data-modal__title">${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.allowed.content.mustRunOnUser }</div>
										<div class="needs-user-data-modal__sentence">
											${ replaceCurlyBraces( item.select_option_name, '<span>', '</span>' ) }
										</div>
										<div class="needs-user-data-modal__disclaimer">
											<div class="description">${ UncannyAutomator._core.i18n.userSelector.userDataModal.action.allowed.content.description }</div>
										</div>
									</div>
								`,
								buttons: {
									cancel:  UncannyAutomator._core.i18n.cancel,
									confirm: UncannyAutomator._core.i18n.userSelector.userDataModal.action.allowed.confirmButton,
								},
							}, {
								size: 'large'
							});

							modal.setEvents({
								onConfirm: () => {
									let modalElements = modal.getElements();

									// Add loading class to button
									modalElements.modal.box.buttons.confirm.addClass( 'uap-btn--loading' ).prop( 'disabled', true );

									// Do an AJAX call to set the global recipe requires user
									recipeSetRequiresUser(
										// Set to true (requires user)
										true,
										// On success
										( response ) => {
											// Do the request
											addItemREST(() => {
												// On success, close and destroy the modal
												modal.destroy();
											}, () => {
												// On fail
												// Remove loading class to button
												modalElements.modal.box.buttons.confirm.removeClass( 'uap-btn--loading' ).prop( 'disabled', false );
											});
										},
										// On fail
										( response ) => {
											// Remove loading class to button
											modalElements.modal.box.buttons.confirm.removeClass( 'uap-btn--loading' ).prop( 'disabled', false );
										}
									);

									// Do a call to set the recipe to draft
									if ( actionWillSetRecipeToDraft ){
										UncannyAutomator.recipe.publish.statusToggle.setValue( false );
									}
								},
								onCancel: () => {
									// Close and destroy modal
									modal.destroy();
								},
							});
						}
					}
					else {
						// Do the request
						addItemREST();
					}
				});

				// Return the
				return $item;
			}));
		}
		else {
			// Remove the available items container
			$itemsList.available.remove();
		}

		// Check if it has unavailable items
		if ( items.hasUnavailableItems ){
			// Append unavailable items to the list container
			$itemsList.unavailable.list.append( items.unavailable.map(( item ) => {

				// Create the UTM medium
				const utm_medium = `unavailable_item-${ this.integrationID }-${ this.integrationID }_${ replaceCurlyBraces( item.name, '', '' ).toLowerCase().replace( /\s+|{|}/g, '_' ) }`;

				// Get the integration name
				const integrationName = isDefined( UncannyAutomator.integrations[ this.integrationID ] ) ? UncannyAutomator.integrations[ this.integrationID ].name : '';

				const upgradeToProURL = addParametersToURL(
					addParametersUTM( 'https://automatorplugin.com/pricing/', {
						'medium':   'select_an_item',
						'campaign': 'update_to_pro',
						'content':  utm_medium,
					} ),
					{
						uap_bpro_context: this.itemType, // "trigger" | "action"
						uap_bpro_integration_id: this.integrationID, // unique ID, ex. WC
						uap_bpro_integration_name: integrationName, // ex. WooCommerce
						uap_bpro_item: item.name
					}
				);

				// Create container
				const $item = $( `
					<a href="${ upgradeToProURL }"
						target="_blank"
						class="item-task-selector__option item-task-selector__option--pro"
						data-search="${ item.name.toLowerCase().replace( /\s+|{|}/g, '' ) }">
						<div class="item-task-selector__option-sentence" dir="auto">
							<uo-icon id="lock"></uo-icon>
							${ replaceCurlyBraces( item.name, '<span class="item-task-selector__option-variable">', '</span>' ) }
							<span class="item-task-selector-option__badge">${ UncannyAutomator._core.i18n.proOnly.pro }</span>
						</div>
					</a>
				` );

				return $item;
			}));
		}
		else {
			// Remove the unavailable items container
			$itemsList.unavailable.container.remove();
		}

		// Get the list of items
		const $items = $itemsList.container.find( '.item-task-selector__option:not(.item-task-selector__option--no-results)' );

		// Listen changes on the search field
		$searchField.on( 'input', () => {
			// Get the current value
			const query = $searchField.val().toLowerCase().replace( /\s+|{|}/g, '' );

			// Reset before searching
			$items.removeClass( 'item-task-selector__option--show item-task-selector__option--hide' );

			// Create variable to evaluate later if there were results
			let hasResults = true;

			// Check if the query is not empty
			if ( ! isEmpty( query ) ){
				// If value it isn't empty then we have to check if there are going to be results, so false by default
				hasResults = false;

				$.each( $items, ( index, item ) => {
					let $item = $( item ),
						text  = $item.data( 'search' );

					if ( text.search( query ) !== -1 ){
						$item.addClass( 'item-task-selector__option--show' );

						// At least one result, so true
						hasResults = true;
					}
					else {
						$item.addClass( 'item-task-selector__option--hide' );
					}
				});
			}

			// Show and hide noResults
			$itemsList.noResults.removeClass( 'item-task-selector__option--hide item-task-selector__option--show' );

			if ( ! hasResults ){
				$itemsList.noResults.addClass( 'item-task-selector__option--show' );
			}
			else {
				$itemsList.noResults.addClass( 'item-task-selector__option--hide' )
			}
		});

		// Append "Cancel" button
		$container.append( this.createCancelButton() );

		return $container;
	}

	openFirstGroupsOfFields( itemID ) {
		// Check if the ID of the new item is defined
		if ( isDefined( itemID ) ){
			// Get item form element
			const $newItem = getDOMElements( 'item', {
				id: itemID
			});

			if ( ! isDefined( $newItem ) ) {
				return;
			}

			// Determine if the item is already valid
			// This will be true only if the new item doesn't have
			// options, or all the options have a default value
			const isValid = UncannyAutomator.recipe.items[ itemID ].isValid;

			// Check if it's valid
			if ( isValid ){
				// Check if the recipe is NOT live
				if ( ! UncannyAutomator._recipe.is_recipe_on ){
					// Set the item to live
					$newItem.item
						.find( '.item-status input[type="checkbox"]' )
						.prop( 'checked', true )
						.trigger( 'change' );
				}
			}
			else {
				// Get first token and trigger a click
				// This will open the first group of options
				$newItem.item
					.find( '.item-title__token:not(.item-title__token--filled)' )
					.first()
					.trigger( 'click' );
			}
		}
	}

	createCancelButton(){

		let $cancelButton = document.createElement( 'uo-button' );
		$cancelButton.setAttribute( 'color', 'secondary' );
		$cancelButton.innerText = UncannyAutomator._core.i18n.cancel;

		$cancelButton.addEventListener( 'click', () => {
			new ItemCreatorIntegrations( this.itemType, this.$item, this.attributes );
		});

		return $cancelButton;
	}

	removeConnectingIntegrationsParameters(){
		// If the user is seeing this screen it's because the modal is closed,
		// or because the user already connected the integration
		addParametersToURL( window.location.href, {
			ua_connecting_integration:    '',
			ua_connecting_integration_id: '',
			ua_connecting_integration_context: '',
			ua_connecting_integration_condition: '',
		}, {
			setAsURL: true,
			refresh: false
		});
	}
}

export default ItemCreatorTaskSelector;
